<template>
  <VLoader v-if="loading" />
  <section v-else class="shared-section product-details">
    <!-- header  -->
    <header class="shared-section-header">
      <h2 class="shared-section-header-heading">
        <router-link :to="{ name: 'products' }">
          {{ $t('productDetails.header.heading') }}
        </router-link>
      </h2>
      <span class="mx-4">
        <VSvgIcon name="chevron-double-right" />
      </span>
      <span class="font-bold">{{ productName }}</span>
    </header>

    <!-- hero -->
    <div class="product-details-hero">
      <div class="w-full px-6 mb-8 sm:max-w-md lg:mr-20 lg:px-0">
        <img :src="product.thumbnail" />
      </div>
      <div class="w-full md:max-w-xl lg:max-w-md">
        <ProductDetailsCard
          :product="product"
          :pendingSubscriptions="pendingSubscriptions"
          :licensesKeys="licensesKeys"
        />
      </div>
    </div>
    <div class="mb-4" v-if="isChromaProduct(product.display_name) && hasInspire()">
      <div class="product-details-heading-container mb-4">
        <h4 class="product-details-heading">
          <span>{{ $t('productDetails.card.chroma_promo.note') }}</span>
        </h4>
      </div>
      <div class="mb-4">
        <div class="flex flex-col lg:flex-row border border-slate-200">
          <div class="lg:w-4/12">
            <img src="../../assets/chroma-free-trial.png" class="w-full h-full object-cover" />
          </div>
          <div class="lg:w-8/12 p-5 bg-white">
            <h3 class="text-lg font-bold mb-3">
              {{ $t('productDetails.card.chroma_promo.title') }}
            </h3>
            <p class="text-base mb-3" v-html="$t('productDetails.card.chroma_promo.content')" />
            <a
              href="https://www.chromaembroidery.com/legacy-license"
              target="_blank"
              class="p-5 flex gap-3 justify-center items-center orange-background text-white font-bold rounded-md w-64"
            >
              {{ $t('productDetails.card.chroma_promo.button') }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <!-- pending subscription -->
    <div v-if="!!licences.length && false" class="mb-4">
      <div class="product-details-heading-container">
        <h4 class="border-b-0 product-details-heading">
          <span v-if="isChromaLegacyProduct()">
            {{ $t('productDetails.pendingSubscriptionsLegacy.heading') }}
          </span>
          <span v-else>
            {{ $t('productDetails.pendingSubscriptions.heading') }}
          </span>
        </h4>
        <span
          class="hidden text-sm font-semibold text-blue-500 cursor-pointer sm:block"
          @click="showLimitLicences = !showLimitLicences"
        >
          {{
            showLimitLicences
              ? $t('productDetails.normal_videos.action1')
              : $t('productDetails.normal_videos.action2')
          }}
        </span>
      </div>
      <div class="mb-4 p-2">
        <div class="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 pt-8 pb-4">
          <div
            v-for="pendingSubscription in licences"
            :key="`pending-subscription-card-${pendingSubscription.id}`"
            class="rounded-md shadow-md p-4"
          >
            <div class="text-right">
              <button class="rounded-full" @click="() => goToChroma(pendingSubscription.uuid)">
                <VSvgIcon name="openInNew" class="h-5" />
              </button>
            </div>
            <div class="capitalize font-bold">{{ pendingSubscription.plan }}</div>
            <div>
              {{
                pendingSubscription.serialNumber
                  ? pendingSubscription.serialNumber
                  : pendingSubscription.uuid
              }}
            </div>
            <div class="text-xs text-gray-500" v-if="pendingSubscription.purchaseDate">
              Purchased Date: {{ formatDate(pendingSubscription.purchaseDate) }}
            </div>
          </div>
        </div>
      </div>
      <span
        class="block w-1/2 p-4 mx-auto text-lg font-semibold text-center text-blue-600 border border-blue-600 rounded-full cursor-pointer sm:hidden"
        @click="showLimitLicences = !showLimitLicences"
      >
        {{
          showLimitLicences
            ? $t('productDetails.videos.action1')
            : $t('productDetails.videos.action2')
        }}
      </span>
    </div>
    <!-- resources -->
    <div
      id="resources"
      v-if="
        !!resources.length ||
        isChromaProduct() ||
        isDtgProduct() ||
        isLuminProduct() ||
        isVisionProduct() ||
        isRicomaHeatPressProduct() ||
        !!productResources.length
      "
      class="mb-4"
    >
      <h4 class="product-details-heading">{{ $t('productDetails.resources.heading') }}</h4>
      <div class="product-details-resources gap-12">
        <div v-if="isRicomaHeatPressProduct()" class="product-details-resource">
          <a
            :href="pathDownloadFirmwareHeatPress"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Firmware` }}
          </h5>
        </div>
        <div v-if="isVisionProduct()" class="product-details-resource">
          <a
            :href="pathDownloadVision"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} VividRip` }}
          </h5>
        </div>
        <div v-if="isVisionProduct()" class="product-details-resource">
          <a
            href="https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Vision/Vision_DTG_Safety_and_Quick_Installation_Guide.pdf"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="file" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Safety and Quick Installation Guide` }}
          </h5>
        </div>
        <div v-if="isVisionProduct()" class="product-details-resource">
          <a
            href="https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Vision/Vision_DTG_Maintnance_Guide.pdf"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="file" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Maintnance Guide` }}
          </h5>
        </div>
        <div v-if="isDtgProduct()" class="product-details-resource">
          <a
            :href="pathDownloadFirmwareDTG"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Firmware Version ${firmwareVersion}` }}
          </h5>
        </div>
        <div v-if="isDtgProduct()" class="product-details-resource">
          <a
            href="https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/DTG-Firmware/Ricoh-splash-screen-temporary-fix/Ri1000%20Splash%20Screen%20Temporary%20Fix.zip"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Ricoh splash screen temporary fix` }}
          </h5>
        </div>
        <div v-if="isDtgProduct()" class="product-details-resource">
          <a :href="pathDownloadDtg" target="_blank" class="product-details-resource-link" download>
            <div class="product-details-resource-icon">
              <VSvgIcon name="file" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{
              `${$t('productDetails.resources.download')} ${$t('productDetails.resources.heading')}`
            }}
          </h5>
        </div>
        <div v-if="isChromaProduct()" class="product-details-resource">
          <a
            :href="pathDownloadChroma"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Software` }}
          </h5>
        </div>
        <div v-if="isLuminProduct()" class="product-details-resource">
          <a
            :href="pathDownloadLumin"
            target="_blank"
            class="product-details-resource-link"
            download
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} Software` }}
          </h5>
        </div>
        <div
          v-for="resource in resources"
          :key="`resource-${resource.id}`"
          class="product-details-resource"
        >
          <a :href="resource.file_path" target="_blank" class="product-details-resource-link">
            <div class="product-details-resource-icon">
              <img :src="getResourceIcon(resource.type_name)" :alt="resource.type_name" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.download')} ${resource.type_name} ` }}
          </h5>
        </div>
        <div v-if="isChromaProduct()" class="product-details-resource">
          <a :href="pathYoutubeChannelChroma" target="_blank" class="product-details-resource-link">
            <div class="product-details-resource-icon">
              <VSvgIcon name="watch" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${$t('productDetails.resources.watch')}` }}
          </h5>
        </div>
        <div
          v-for="resource in productResources"
          :key="`product-resource-${resource.id}`"
          class="product-details-resource"
        >
          <a
            :href="resource.file_path ? resource.file_path : resource.url"
            target="_blank"
            class="product-details-resource-link"
          >
            <div class="product-details-resource-icon">
              <VSvgIcon name="download" width="50" height="50" class="text-white" />
            </div>
          </a>
          <h5 class="product-details-resource-heading">
            {{ `${resource.title} ` }}
          </h5>
        </div>
      </div>
    </div>

    <!-- videos -->
    <div v-if="!!videos.length" class="mb-4">
      <div class="product-details-heading-container">
        <h4 class="border-b-0 product-details-heading">
          {{ $t('productDetails.normal_videos.heading') }}
        </h4>
        <span
          class="hidden text-sm font-semibold text-blue-500 cursor-pointer sm:block"
          @click="showLimitVideos = !showLimitVideos"
        >
          {{
            showLimitVideos
              ? $t('productDetails.normal_videos.action1')
              : $t('productDetails.normal_videos.action2')
          }}
        </span>
      </div>
      <div class="product-details-videos">
        <ProductDetailsVideo
          v-for="(video, index) in videos"
          :key="`video-id-${index}`"
          :video="video"
        />
      </div>

      <!-- show on sm screen -->
      <span
        class="block w-1/2 p-4 mx-auto text-lg font-semibold text-center text-blue-600 border border-blue-600 rounded-full cursor-pointer sm:hidden"
        @click="showLimitVideos = !showLimitVideos"
      >
        {{
          showLimitVideos
            ? $t('productDetails.videos.action1')
            : $t('productDetails.videos.action2')
        }}
      </span>
    </div>

    <!-- traninig videos -->
    <div v-if="!!trainingVideos.length" class="mb-4">
      <div class="product-details-heading-container">
        <h4 class="border-b-0 product-details-heading">
          {{
            isChromaProduct() || isDtgProduct()
              ? $t('productDetails.videos.heading_chroma')
              : $t('productDetails.videos.heading')
          }}
        </h4>
        <span
          class="hidden text-sm font-semibold text-blue-500 cursor-pointer sm:block"
          @click="showLimit = !showLimit"
        >
          {{
            showLimit ? $t('productDetails.videos.action1') : $t('productDetails.videos.action2')
          }}
        </span>
      </div>
      <div class="product-details-videos">
        <ProductDetailsVideo
          v-for="(video, index) in trainingVideos"
          :key="`vid-${index}`"
          :video="video"
        />
      </div>

      <!-- show on sm screen -->
      <span
        class="block w-1/2 p-4 mx-auto text-lg font-semibold text-center text-blue-600 border border-blue-600 rounded-full cursor-pointer sm:hidden"
        @click="showLimit = !showLimit"
      >
        {{ showLimit ? $t('productDetails.videos.action1') : $t('productDetails.videos.action2') }}
      </span>
    </div>

    <!-- accessories -->
    <div v-if="!!accessories.length">
      <h4 class="product-details-heading">
        {{ $t('productDetails.accessories.heading') }}
      </h4>
      <div class="product-details-accessories">
        <div v-for="acc in accessories" :key="`acc-${acc.id}`" class="product-details-accessory">
          <img
            :src="acc.thumbnail_path"
            :alt="acc.name"
            class="object-contain object-center h-64 mx-auto mb-4"
          />
          <div class="mb-2 text-lg font-semibold">{{ acc.name }}</div>
          <p>{{ acc.description }}</p>
          <a
            :href="acc.shop_link"
            target="_blank"
            class="flex items-center inline-block py-2 text-sm font-semibold text-blue-500"
          >
            {{ $t('productDetails.accessories.link') }}
            <VSvgIcon name="arrow-right" class="w-5 ml-1" />
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';

import { format, parseISO } from 'date-fns';
import { isChromaLegacy, isChroma, isDtg, isLumin, safeNullDataset, isVision } from '@/utils';
import { axiosClient } from '../../api';

import partsBook from '@/assets/parts-book.svg';
import quickStartQuide from '@/assets/quick-start-guide.svg';

import ProductDetailsCard from '@/components/customer/ProductDetailsCard';
import ProductDetailsVideo from '@/components/customer/ProductDetailsVideo';
import VLoader from '@/components/base/VLoader';
import VSvgIcon from '@/components/base/VSvgIcon';

export default {
  name: 'ProductDetails',
  components: { ProductDetailsCard, ProductDetailsVideo, VSvgIcon, VLoader },
  data() {
    return {
      loading: false,
      showLimit: true,
      showLimitVideos: true,
      showLimitLicences: true,
      show: false,
      pendingSubscriptions: [],
      licensesKeys: [],
    };
  },
  computed: {
    ...mapState({
      product: state => state.customer.product ?? {},
      language: state => state.language,
    }),
    trainingVideos() {
      const videos = safeNullDataset(this.product)(['training_videos', 'data']);
      return this.showLimit ? videos.slice(0, 4) : videos;
    },
    resources() {
      return safeNullDataset(this.product)(['resources', 'data']);
    },
    productResources() {
      return safeNullDataset(this.product)(['productResources', 'data']);
    },
    videos() {
      const videos = safeNullDataset(this.product)(['videos', 'data']);
      return this.showLimitVideos ? videos.slice(0, 4) : videos;
    },
    licences() {
      if (isChromaLegacy(this.productName)) {
        return this.showLimitLicences
          ? this.pendingSubscriptions.filter(p => p.perpetualSerialNumber).slice(0, 4)
          : this.pendingSubscriptions.filter(p => p.perpetualSerialNumber);
      }
      return this.showLimitLicences
        ? this.pendingSubscriptions.filter(p => !p.perpetualSerialNumber).slice(0, 4)
        : this.pendingSubscriptions.filter(p => !p.perpetualSerialNumber);
    },
    accessories() {
      return safeNullDataset(this.product)(['accessories', 'data']);
    },
    productName() {
      const name = (this.product && this.product.display_name) || '';
      return name;
    },
    pathDownloadDtg() {
      return 'https://ricohdtg.com/downloads/';
    },
    pathDownloadFirmwareHeatPress() {
      return 'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/heat-presses-firmware/firmware_heat_press.zip';
    },
    pathDownloadFirmwareDTG() {
      return this.productName.indexOf('1000') >= 0
        ? 'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/DTG-Firmware/Ri_1000_Updater.zip'
        : 'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/DTG-Firmware/Ri_2000_Updater.zip';
    },
    firmwareVersion() {
      return this.productName.indexOf('1000') >= 0 ? '01.37.2' : '01.25.00';
    },
    pathDownloadVision() {
      const pkg =
        'https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Softwares/VividRipSetup.pkg';
      const exe =
        'https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Softwares/VividRipSetup.exe';
      const detectOS = version => navigator.appVersion.indexOf(version) != -1;
      return detectOS('Mac') || detectOS('Linux') ? pkg : exe;
    },
    pathDownloadChroma() {
      const pkg =
        'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/Softwares/ChromaSetup.pkg';
      const exe =
        'https://ricoma-customer-portal.nyc3.digitaloceanspaces.com/Softwares/ChromaSetup.exe';
      const detectOS = version => navigator.appVersion.indexOf(version) != -1;
      return detectOS('Mac') || detectOS('Linux') ? pkg : exe;
    },
    pathDownloadLumin() {
      const pkg =
        'https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Softwares/VividRipSetup.pkg';
      const exe =
        'https://ricoma-customer-portal.nyc3.cdn.digitaloceanspaces.com/Softwares/VividRipSetup.exe';
      const detectOS = version => navigator.appVersion.indexOf(version) != -1;
      return detectOS('Mac') || detectOS('Linux') ? pkg : exe;
    },
    pathYoutubeChannelChroma() {
      return 'https://www.youtube.com/playlist?list=PLipwr7p9jbflyulg7CPHkziBLgSlX8mCs';
    },
  },
  methods: {
    async getProductData() {
      this.loading = true;
      await this.$store.dispatch('customer/getProduct', {
        lang_code: this.language.code,
        productId: this.$route.params.id,
        includeVideos: true,
        includeResources: true,
        includeAccessories: true,
      });
      try {
        if (!this.$user.customer) return;
        const response = await axiosClient.get(`product/chroma-pending-subscriptions`);
        //`${process.env.VUE_APP_CHROMA_API_BASE_URL}/api/v1/subscriptions/local/netsuite-customers/${this.$user.customer.internal_id}`,
        //const response = await fetch(
        //`${process.env.VUE_APP_CHROMA_API_BASE_URL}/api/v1/pending-subscriptions?where={"netsuiteCustomerId": ${this.$user.customer.internal_id},"subscriptionId": {"isNull":true}}&x-ricoma-domain=https://ricoma.com`,
        //);
        if (response.status === 200) {
          this.pendingSubscriptions = response.data;
        }
        //const responseCustomers = await fetch(
        //`${process.env.VUE_APP_CHROMA_API_BASE_URL}/api/v1/customers?where={"email": "${this.$user.customer.email}"}&x-ricoma-domain=https://ricoma.com`,
        //);
        //const customerId = (await responseCustomers.json()).data[0]?.id;
        //`${process.env.VUE_APP_CHROMA_API_BASE_URL}/api/v1/licenses/netsuite-customers/${this.$user.customer.internal_id}`,
        //const responseLicenses = await fetch(
        //`${process.env.VUE_APP_CHROMA_API_BASE_URL}/api/v1/subscriptions?where={"customerId": ${customerId}}&x-ricoma-domain=https://ricoma.com`,
        //);
        const responseLicenses = await axiosClient.get(`product/chroma-subscriptions`);
        if (responseLicenses.status === 200) {
          this.licensesKeys = responseLicenses.data;
        }
      } catch (e) {
        console.log(e);
      }
      this.loading = false;
    },
    formatDate(date) {
      return format(parseISO(date), 'MM/dd/yyyy') || '-';
    },
    async goToChroma(uuid) {
      window.open(
        `https://www.chromaembroidery.com/pending-subscription/activate?subscriptionUUID=${uuid}`,
        '_blank',
      );
    },
    getResourceIcon(name) {
      const icons = { 'parts-book': partsBook, 'quick-start-guide': quickStartQuide };
      return icons[name.replace(/ /g, '-').toLowerCase()];
    },
    isChromaProduct() {
      return isChroma(this.productName);
    },
    isChromaLegacyProduct() {
      return isChromaLegacy(this.productName);
    },
    isLuminProduct() {
      return isLumin(this.productName);
    },
    isDtgProduct() {
      return isDtg(this.productName);
    },
    isVisionProduct() {
      return isVision(this.productName);
    },
    isRicomaHeatPressProduct() {
      if (!this.product?.netsuite_name) return false;
      const netsuite_name = this.product.netsuite_name.trim().toLowerCase();
      return (
        netsuite_name.includes('hp-1515f-t (s)') ||
        netsuite_name.includes('hp-1620f-t (s)') ||
        netsuite_name.includes('hp-0506fpt (s)') ||
        netsuite_name.includes('hp-1112hm (s)') ||
        netsuite_name.includes('hp-0408fc (s)')
      );
    },
    hasInspire() {
      return this.product.orders.some(o => o.tier.toLowerCase() === 'inspire');
    },
  },
  mounted() {
    this.getProductData();
  },
};
</script>
<style>
.orange-background {
  background-image: linear-gradient(90deg, #ff7200, #fc9500);
}
</style>
